import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LoadingType } from "../../../types/CommonTypes";
import { getSalesGoalList, saveSalesGoal as saveSalesGoalApi, updateSalesGoal as updateSalesGoalApi, SalesGoalPayload } from "../../../service/SalesGoalService";
import { useAppSelector } from "../redux-hooks";
import { SalesGoalRecord } from "../../../components/operation/isr/isrTypes";

type SalesGoalState = {
  salesGoalList: SalesGoalRecord[];
  fetchSalesGoalListStatus: LoadingType;
  saveSalesGoalStatus: LoadingType;
  updateSalesGoalStatus: LoadingType;
};

const initialState: SalesGoalState = {
  salesGoalList: [],
  fetchSalesGoalListStatus: "idle",
  saveSalesGoalStatus: "idle",
  updateSalesGoalStatus: "idle",
};

const mapSalesGoalRecord = (row: any): SalesGoalRecord => ({
  salesGoalId: row?.salesGoalId,
  year: row?.year != null ? String(row.year) : "",
  month: row?.month ?? "",
  invoiceYear: row?.invoiceYear != null ? String(row.invoiceYear) : "",
  collectionTarget: row?.collectionTarget != null ? String(row.collectionTarget) : "",
  actualCollectedAmt: row?.actualCollectedAmt != null ? String(row.actualCollectedAmt) : "",
  achievementPct: row?.achievementPct != null ? String(row.achievementPct) : "",
});

export const fetchSalesGoalList = createAsyncThunk("fetchSalesGoalList", async () => {
  const response = await getSalesGoalList();
  return response.data;
});

export const saveSalesGoal = createAsyncThunk("saveSalesGoal", async (payload: SalesGoalPayload) => {
  const response = await saveSalesGoalApi(payload);
  return response.data;
});

export const updateSalesGoal = createAsyncThunk("updateSalesGoal", async (payload: SalesGoalPayload) => {
  const response = await updateSalesGoalApi(payload);
  return response.data;
});

const salesGoalSlice = createSlice({
  name: "salesGoal",
  initialState,
  reducers: {
    resetSalesGoalSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSalesGoalList.pending, (state) => {
        state.fetchSalesGoalListStatus = "loading";
      })
      .addCase(fetchSalesGoalList.fulfilled, (state, action) => {
        state.fetchSalesGoalListStatus = "success";
        const rows = action.payload?.data;
        state.salesGoalList = Array.isArray(rows) ? rows.map(mapSalesGoalRecord) : [];
      })
      .addCase(fetchSalesGoalList.rejected, (state) => {
        state.fetchSalesGoalListStatus = "error";
      })
      .addCase(saveSalesGoal.pending, (state) => {
        state.saveSalesGoalStatus = "loading";
      })
      .addCase(saveSalesGoal.fulfilled, (state) => {
        state.saveSalesGoalStatus = "success";
      })
      .addCase(saveSalesGoal.rejected, (state) => {
        state.saveSalesGoalStatus = "error";
      })
      .addCase(updateSalesGoal.pending, (state) => {
        state.updateSalesGoalStatus = "loading";
      })
      .addCase(updateSalesGoal.fulfilled, (state) => {
        state.updateSalesGoalStatus = "success";
      })
      .addCase(updateSalesGoal.rejected, (state) => {
        state.updateSalesGoalStatus = "error";
      });
  },
});

export const { resetSalesGoalSlice } = salesGoalSlice.actions;
export const useSalesGoalReducer = () => useAppSelector((state) => state.salesGoalSlice);
export default salesGoalSlice.reducer;
